import type { ReactNode } from 'react';

/**
 * O vocabulário do molde dos painéis (onda 318, spec 06b, RN-256).
 *
 * ── Um painel por área, todos com as mesmas peças ─────────────────────
 *
 * «Um painel por área. Agora o do LabManager. Depois Campo, Marketing,
 * CRM… No Hub, um painel Laboratório + Campo» (Humberto, 08/10/2026). Em
 * vez de cada área inventar o seu, as peças moram aqui, em
 * `web/src/painel/`, e NÃO SABEM de área nenhuma: recebem `Pendencia[]` e
 * `Indicador[]` prontos. Quem sabe da API é o adaptador de cada área
 * (`doLaboratorio.ts` para o LabManager), que traduz os dados dela nestes
 * dois formatos. Painel novo = adaptador novo; as peças não mudam.
 *
 * Este arquivo tem os tipos, as palavras fixas do resumo e da gravidade, e
 * a ordem da pilha. Fica sem nenhuma dependência de tela (o React entra
 * só como tipo), para a ordem poder ser rodada e conferida pelo teste
 * (305.11) sem montar componente nenhum.
 */

/**
 * A gravidade de uma situação: o que a cor do cartão e a palavra dizem.
 *
 * Vem do TIPO da situação, nunca de um corte de dias: quantos dias parados
 * tornam algo grave é decisão da Testesolo e está aberta (PD-75).
 */
export type Gravidade = 'trava' | 'atencao' | 'saber';

/** Os quatro números do resumo — e os quatro filtros da pilha. */
export type GrupoDoResumo = 'travado' | 'esperando' | 'atrasado' | 'vence';

/**
 * Uma situação: um cartão da pilha.
 *
 * `numero` conta ITENS (ensaios, amostras, OS…); o resumo conta SITUAÇÕES
 * (cartões), porque somar equipamento com amostra e com OS daria um número
 * sem unidade. Cartão com `numero` zero não aparece.
 */
export interface Pendencia {
  /** Identifica a situação entre uma visita e outra (M2) e nos testes. */
  chave: string;
  /**
   * Quantos itens a situação junta. `null` quando o cartão é de um registro
   * só — o Hub (onda 550) mostra uma conversa, uma proposta, uma tarefa —, e
   * aí o cartão não tem o número: «1» em todo cartão seria ruído.
   */
  numero: number | null;
  /** O que é, em até 8 palavras. */
  titulo: string;
  /** A consequência, numa linha. */
  consequencia: string;
  /** Um detalhe: o mais parado, um exemplo. */
  detalhe?: string;
  gravidade: Gravidade;
  /**
   * A palavra da gravidade, quando quem monta a pilha tem a sua escada: o Hub
   * fala a do Radar (Crítico, Em risco, Em andamento), e não a do Painel. A
   * cor continua a da `gravidade`. Ausente: `PALAVRA_DA_GRAVIDADE`.
   */
  palavra?: string;
  /** A etiqueta da área («Laboratório», «Equipamentos»…) e o chip que filtra. */
  area: string;
  /** Em qual dos quatro números do resumo a situação conta (nenhum, se omitido). */
  grupo?: GrupoDoResumo;
  /** Onde se resolve: o botão no PC; no celular, o cartão inteiro. */
  acao: { texto: string; para: string };
  /** O porquê, que antes ficava escrito na tela: vai para o «i». */
  explicacao?: ReactNode;
  /**
   * A permissão de quem resolve — o «Comigo» (M1) mostra só as que a pessoa
   * tem. `'a|b'` quando qualquer uma das duas basta: a pilha entende o `|`,
   * e quem monta o painel passa o `pode` da sessão como ele é.
   */
  permissao?: string;
  /** Há quantos dias o mais parado espera; `null` quando a API não sabe. */
  parado?: number | null;
}

/** Um número do Andamento, com a comparação ao período anterior. */
export interface Indicador {
  chave: string;
  rotulo: string;
  /** Já formatado («+62», «9», «86», «—»). */
  valor: string;
  unidade?: string;
  detalhe?: string;
  /** Escrita em palavra («cresceu», «2 dias a menos»); o tom só reforça. */
  comparacao?: { texto: string; tom: 'bom' | 'ruim' | 'neutro' };
  /** A série do minigráfico (M4), da mais antiga à mais nova. */
  serie?: number[];
}

/** A gravidade em palavra: a cor nunca é a única pista (briefing §18). */
export const PALAVRA_DA_GRAVIDADE: Record<Gravidade, string> = {
  trava: 'Trava',
  atencao: 'Atenção',
  saber: 'Para saber',
};

/** Os quatro números do resumo, na ordem da tela. */
export const GRUPOS_DO_RESUMO: readonly { grupo: GrupoDoResumo; rotulo: string }[] = [
  { grupo: 'travado', rotulo: 'Travado' },
  { grupo: 'esperando', rotulo: 'Esperando alguém' },
  { grupo: 'atrasado', rotulo: 'Passou da data' },
  { grupo: 'vence', rotulo: 'Vence em 30 dias' },
];

/** Só as situações que existem: cartão com zero não aparece (nem conta). */
export function comNumero(p: readonly Pendencia[]): Pendencia[] {
  return p.filter((x) => Number(x.numero) > 0);
}

/**
 * A ordem fixa da pilha (RN-256, 3): primeiro o que trava, depois o que
 * pede atenção, por fim o que é para saber. Na mesma gravidade, o que está
 * parado há mais tempo vem antes — o que não tem esse dado vai para o fim
 * da sua gravidade, porque «não sei há quanto tempo» não é «há pouco». No
 * empate, o maior número. Ordena uma cópia: a lista de quem chama não muda.
 */
export function ordenarPendencias(p: Pendencia[]): Pendencia[] {
  const ordem: Gravidade[] = ['trava', 'atencao', 'saber'];
  const peso = (g: Gravidade) => {
    const i = ordem.indexOf(g);
    return i < 0 ? ordem.length : i;
  };
  const parado = (x: Pendencia) => (
    typeof x.parado === 'number' && Number.isFinite(x.parado) ? x.parado : null);

  return [...p].sort((a, b) => {
    const g = peso(a.gravidade) - peso(b.gravidade);
    if (g !== 0) return g;
    const pa = parado(a);
    const pb = parado(b);
    if (pa !== pb) {
      if (pa === null) return 1;
      if (pb === null) return -1;
      return pb - pa;
    }
    return Number(b.numero) - Number(a.numero);
  });
}
